import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Eye, FileDown, Link2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export const TearSheetDialog = ({ open, onClose }) => {
  const { company } = useAuth();
  const [share, setShare] = useState(null);
  const [gateError, setGateError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open || !company) return;
    setGateError(null);
    api.post(`/companies/${company.id}/tear-sheet/share`)
      .then((r) => setShare(r.data))
      .catch((e) => setGateError(e?.response?.data?.detail || "Could not load share link"));
  }, [open, company]);

  const shareUrl = share ? `${process.env.REACT_APP_BACKEND_URL}/api/ts/${share.token}` : "";

  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl);
    toast.success("Share link copied");
  };

  const rotate = async () => {
    try {
      const { data } = await api.post(`/companies/${company.id}/tear-sheet/share/rotate`);
      setShare((s) => ({ ...s, token: data.token }));
      toast.success("New link issued - the old one no longer works");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reset link"); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/companies/${company.id}/tear-sheet`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(company.name || "company").replace(/\s+/g, "_")}_tear_sheet.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Tear sheet downloaded");
    } catch (e) { console.error(e); toast.error("Could not generate tear sheet"); }
    finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="tear-sheet-dialog">
        <DialogHeader>
          <DialogTitle>Investor tear sheet</DialogTitle>
          <DialogDescription>A live one-page PDF of your traction, round and asks - always built from your latest data.</DialogDescription>
        </DialogHeader>
        {gateError ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="tear-sheet-gate-error">{gateError}</p>
        ) : (
          <div className="space-y-4">
            <Button onClick={download} disabled={downloading} className="w-full gap-2" data-testid="tear-sheet-download">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Download PDF
            </Button>
            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-foreground"><Link2 className="h-3.5 w-3.5" /> Tracked share link</div>
              <div className="flex gap-2">
                <Input readOnly value={shareUrl} className="h-9 text-xs" data-testid="tear-sheet-share-url" />
                <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={copyLink} disabled={!share} data-testid="tear-sheet-copy-link">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
              </div>
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                Viewers enter their email before the PDF opens - every identified open is logged below.{" "}
                <button onClick={rotate} className="font-medium text-primary hover:underline" data-testid="tear-sheet-rotate">Reset link</button>
              </p>
            </div>
            <div className="flex items-center justify-between rounded-lg border bg-secondary/40 px-3 py-2.5" data-testid="tear-sheet-stats">
              <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Eye className="h-3.5 w-3.5" /> Link opens</span>
              <span className="text-sm font-semibold tabular-nums text-foreground" data-testid="tear-sheet-views">{share ? share.views : "-"}</span>
            </div>
            {share?.recent?.length > 0 && (
              <div className="space-y-1.5" data-testid="tear-sheet-viewers">
                <div className="text-xs font-semibold text-foreground">Recent viewers</div>
                {share.recent.map((v, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
                    <span className="truncate font-medium text-foreground/80">{v.email || "anonymous"}</span>
                    <span className="shrink-0">{new Date(v.at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
            {share?.last_view_at && (
              <p className="text-[11px] text-muted-foreground">Last opened {new Date(share.last_view_at).toLocaleString()}</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
